<template>
    <div class="video-uploader q-pa-md border-rounded bg-grey-1">
        <!-- 4-Step Process Visual Stepper -->
        <div
            class="video-stepper q-mb-md q-pa-md bg-white rounded-borders shadow-1 border-grey"
        >
            <div class="row items-center justify-between no-wrap">
                <!-- Step 1: Upload -->
                <div class="col text-center step-box" :class="getStepClass(1)">
                    <div class="step-icon-container q-mx-auto q-mb-xs">
                        <q-icon
                            v-if="getStepStatus(1) === 'completed'"
                            name="check_circle"
                            color="positive"
                            size="24px"
                        />
                        <q-spinner-cloud
                            v-else-if="
                                getStepStatus(1) === 'active' && uploading
                            "
                            color="warning"
                            size="24px"
                        />
                        <q-icon
                            v-else
                            name="cloud_upload"
                            :color="
                                getStepStatus(1) === 'active'
                                    ? 'warning'
                                    : 'grey-5'
                            "
                            size="24px"
                        />
                    </div>
                    <div
                        class="step-title text-weight-bold"
                        :class="getStepTextClass(1)"
                    >
                        1. {{ $t("onboarding.step_upload") || "Upload" }}
                    </div>
                    <div class="step-caption text-caption">
                        <span
                            v-if="getStepStatus(1) === 'active' && uploading"
                            class="text-warning text-weight-bold"
                            >{{ uploadProgress }}%</span
                        >
                        <span
                            v-else-if="getStepStatus(1) === 'completed'"
                            class="text-positive text-weight-medium"
                            >{{
                                $t("onboarding.completed") || "Completed"
                            }}</span
                        >
                        <span v-else class="text-grey-6">{{
                            $t("onboarding.step_upload_desc") || "Select & send"
                        }}</span>
                    </div>
                </div>

                <div class="step-connector col-1">
                    <q-linear-progress
                        v-if="getStepStatus(1) === 'active' && uploading"
                        :value="uploadProgress / 100"
                        color="warning"
                        size="3px"
                        animated
                    />
                    <div
                        v-else
                        class="connector-line"
                        :class="{
                            'line-done': getStepStatus(1) === 'completed',
                        }"
                    ></div>
                </div>

                <!-- Step 2: Check -->
                <div class="col text-center step-box" :class="getStepClass(2)">
                    <div class="step-icon-container q-mx-auto q-mb-xs">
                        <q-icon
                            v-if="getStepStatus(2) === 'completed'"
                            name="check_circle"
                            color="positive"
                            size="24px"
                        />
                        <q-spinner-dots
                            v-else-if="getStepStatus(2) === 'active'"
                            color="warning"
                            size="24px"
                        />
                        <q-icon
                            v-else-if="getStepStatus(2) === 'failed'"
                            name="error"
                            color="negative"
                            size="24px"
                        />
                        <q-icon
                            v-else
                            name="fact_check"
                            :color="
                                getStepStatus(2) === 'active'
                                    ? 'warning'
                                    : 'grey-5'
                            "
                            size="24px"
                        />
                    </div>
                    <div
                        class="step-title text-weight-bold"
                        :class="getStepTextClass(2)"
                    >
                        2. {{ $t("onboarding.step_check") || "Check" }}
                    </div>
                    <div class="step-caption text-caption">
                        <span
                            v-if="getStepStatus(2) === 'active'"
                            class="text-warning text-weight-bold"
                            >{{
                                $t("onboarding.checking") || "Checking..."
                            }}</span
                        >
                        <span
                            v-else-if="getStepStatus(2) === 'completed'"
                            class="text-positive text-weight-medium"
                            >{{
                                $t("onboarding.completed") || "Completed"
                            }}</span
                        >
                        <span
                            v-else-if="getStepStatus(2) === 'failed'"
                            class="text-negative text-weight-medium"
                            >{{ $t("onboarding.failed") || "Failed" }}</span
                        >
                        <span
                            v-else-if="uploading"
                            class="text-amber-8 text-weight-medium"
                            >{{ $t("onboarding.waiting") || "Waiting" }}</span
                        >
                        <span v-else class="text-grey-6">{{
                            $t("onboarding.step_check_desc") || "Quality check"
                        }}</span>
                    </div>
                </div>

                <div class="step-connector col-1">
                    <q-linear-progress
                        v-if="getStepStatus(2) === 'active'"
                        :value="(currentVideo?.processing_progress || 1) / 100"
                        color="warning"
                        size="3px"
                        animated
                    />
                    <div
                        v-else
                        class="connector-line"
                        :class="{
                            'line-done': getStepStatus(2) === 'completed',
                        }"
                    ></div>
                </div>

                <!-- Step 3: Web Optimization -->
                <div class="col text-center step-box" :class="getStepClass(3)">
                    <div class="step-icon-container q-mx-auto q-mb-xs">
                        <q-icon
                            v-if="getStepStatus(3) === 'completed'"
                            name="check_circle"
                            color="positive"
                            size="24px"
                        />
                        <q-spinner-gears
                            v-else-if="getStepStatus(3) === 'active'"
                            color="warning"
                            size="24px"
                        />
                        <q-icon
                            v-else-if="getStepStatus(3) === 'failed'"
                            name="error"
                            color="negative"
                            size="24px"
                        />
                        <q-icon
                            v-else
                            name="movie_filter"
                            :color="
                                getStepStatus(3) === 'active'
                                    ? 'warning'
                                    : 'grey-5'
                            "
                            size="24px"
                        />
                    </div>
                    <div
                        class="step-title text-weight-bold"
                        :class="getStepTextClass(3)"
                    >
                        3.
                        {{
                            $t("onboarding.step_web_transcode") ||
                            "Web Optimization"
                        }}
                    </div>
                    <div class="step-caption text-caption">
                        <span
                            v-if="getStepStatus(3) === 'active'"
                            class="text-warning text-weight-bold"
                            >{{
                                $t("onboarding.processing") || "Processing..."
                            }}</span
                        >
                        <span
                            v-else-if="getStepStatus(3) === 'completed'"
                            class="text-positive text-weight-medium"
                            >{{
                                $t("onboarding.completed") || "Completed"
                            }}</span
                        >
                        <span
                            v-else-if="getStepStatus(3) === 'failed'"
                            class="text-negative text-weight-medium"
                            >{{ $t("onboarding.failed") || "Failed" }}</span
                        >
                        <span
                            v-else-if="uploading || isProcessing"
                            class="text-amber-8 text-weight-medium"
                            >{{ $t("onboarding.waiting") || "Waiting" }}</span
                        >
                        <span v-else class="text-grey-6">{{
                            $t("onboarding.step_transcode_desc") ||
                            "Browser format"
                        }}</span>
                    </div>
                </div>

                <div class="step-connector col-1">
                    <q-linear-progress
                        v-if="getStepStatus(3) === 'active'"
                        :value="(currentVideo?.processing_progress || 45) / 100"
                        color="warning"
                        size="3px"
                        animated
                    />
                    <div
                        v-else
                        class="connector-line"
                        :class="{
                            'line-done': getStepStatus(3) === 'completed',
                        }"
                    ></div>
                </div>

                <!-- Step 4: Streaming Preparation -->
                <div class="col text-center step-box" :class="getStepClass(4)">
                    <div class="step-icon-container q-mx-auto q-mb-xs">
                        <q-icon
                            v-if="getStepStatus(4) === 'completed'"
                            name="check_circle"
                            color="positive"
                            size="24px"
                        />
                        <q-spinner-hourglass
                            v-else-if="getStepStatus(4) === 'active'"
                            color="warning"
                            size="24px"
                        />
                        <q-icon
                            v-else-if="getStepStatus(4) === 'failed'"
                            name="error"
                            color="negative"
                            size="24px"
                        />
                        <q-icon
                            v-else
                            name="cell_tower"
                            :color="
                                getStepStatus(4) === 'active'
                                    ? 'warning'
                                    : 'grey-5'
                            "
                            size="24px"
                        />
                    </div>
                    <div
                        class="step-title text-weight-bold"
                        :class="getStepTextClass(4)"
                    >
                        4.
                        {{
                            $t("onboarding.step_hls_stream") ||
                            "Streaming Preparation"
                        }}
                    </div>
                    <div class="step-caption text-caption">
                        <span
                            v-if="getStepStatus(4) === 'active'"
                            class="text-warning text-weight-bold"
                            >{{
                                $t("onboarding.processing") || "Processing..."
                            }}</span
                        >
                        <span
                            v-else-if="getStepStatus(4) === 'completed'"
                            class="text-positive text-weight-medium"
                            >{{ $t("onboarding.ready") || "Ready" }}</span
                        >
                        <span
                            v-else-if="getStepStatus(4) === 'failed'"
                            class="text-negative text-weight-medium"
                            >{{ $t("onboarding.failed") || "Failed" }}</span
                        >
                        <span
                            v-else-if="uploading || isProcessing"
                            class="text-amber-8 text-weight-medium"
                            >{{ $t("onboarding.waiting") || "Waiting" }}</span
                        >
                        <span v-else class="text-grey-6">{{
                            $t("onboarding.step_hls_desc") ||
                            "Adaptive playback"
                        }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- View 1: Not Uploading and No Current Video (Select File Form) -->
        <div
            v-if="!uploading && !currentVideo"
            class="q-pa-sm bg-white rounded-borders shadow-1 border-grey"
        >
            <div
                v-if="existingVideoId"
                class="text-subtitle1 text-weight-medium q-mb-sm"
            >
                {{ $t("onboarding.upload_new_video") }}
            </div>
            <q-file
                v-model="selectedFile"
                :label="$t('onboarding.select_video_file')"
                outlined
                accept="video/*"
                bottom-slots
            >
                <template v-slot:prepend>
                    <q-icon name="video_call" />
                </template>
            </q-file>

            <q-input
                v-model="videoTitle"
                :label="$t('onboarding.video_title')"
                outlined
                class="q-mt-sm"
                :rules="[
                    (val) =>
                        !!val ||
                        $t('validation.required') ||
                        'Title is required',
                ]"
            />

            <!-- Optional Poster File Selector in initial form -->
            <q-file
                v-model="selectedPoster"
                :label="
                    $t('onboarding.upload_poster_image') +
                    ' (' +
                    ($t('common.optional') || 'optional') +
                    ')'
                "
                outlined
                dense
                accept="image/*"
                class="q-mt-sm"
                bg-color="white"
            >
                <template v-slot:prepend>
                    <q-icon name="image" />
                </template>
            </q-file>

            <div class="row justify-between items-center q-mt-md">
                <q-btn
                    v-if="existingVideoId && props.initialVideo"
                    flat
                    color="grey-7"
                    :label="$t('button.action.cancel')"
                    @click="cancelReplace"
                />
                <div v-else></div>
                <q-btn
                    color="primary"
                    icon="cloud_upload"
                    :label="$t('onboarding.upload_video')"
                    :disable="!selectedFile || !videoTitle"
                    @click="startChunkedUpload"
                />
            </div>
        </div>

        <!-- View 2: Uploading In Progress (Step 1 Active) -->
        <div
            v-else-if="uploading"
            class="q-pa-md bg-white rounded-borders shadow-1 border-grey"
        >
            <div class="row items-center justify-between q-mb-xs">
                <div class="text-subtitle2 text-warning row items-center">
                    <q-spinner-cloud
                        size="sm"
                        class="q-mr-xs"
                        color="warning"
                    />
                    {{ $t("onboarding.uploading_video") }} (Step 1: Upload)
                </div>
                <div class="text-weight-bold text-warning">
                    {{ uploadProgress }}%
                </div>
            </div>
            <q-linear-progress
                :value="uploadProgress / 100"
                color="warning"
                size="15px"
                stripe
                animated
                class="rounded-borders"
            />
            <div class="text-caption text-grey-7 q-mt-xs row justify-between">
                <span>{{ $t("onboarding.sending_chunk") }}</span>
                <span
                    >{{
                        Math.round(
                            ((currentChunk + 1) * chunkSize) / (1024 * 1024),
                        )
                    }}
                    MB /
                    {{
                        Math.round((selectedFile?.size || 0) / (1024 * 1024))
                    }}
                    MB</span
                >
            </div>
            <div
                v-if="isRetrying"
                class="q-mt-sm q-pa-xs bg-amber-1 rounded-borders row items-center text-amber-10 text-caption"
            >
                <q-spinner-dots size="xs" class="q-mr-xs" color="warning" />
                <span
                    >{{
                        $t("onboarding.retrying_upload") ||
                        "Connection interrupted. Retrying upload..."
                    }}
                    ({{ $t("onboarding.attempt") || "Attempt" }}
                    {{ retryAttemptCount }} / 5)</span
                >
            </div>
        </div>

        <!-- View 3: Video Uploaded & (Checking / Processing / Ready / Failed) -->
        <div v-else-if="currentVideo" class="q-pa-sm">
            <!-- Title Edit Section -->
            <div class="row items-center q-col-gutter-sm q-mb-md">
                <div class="col">
                    <q-input
                        v-model="videoTitle"
                        :label="$t('onboarding.video_title')"
                        outlined
                        dense
                        bg-color="white"
                    />
                </div>
                <div class="col-auto">
                    <q-btn
                        color="primary"
                        icon="save"
                        :label="$t('button.action.save')"
                        :disable="
                            !videoTitle || videoTitle === currentVideo.title
                        "
                        @click="saveTitle"
                    />
                </div>
            </div>

            <!-- Active Stage Progress Card -->
            <div
                v-if="isProcessing"
                class="q-my-md q-pa-md bg-white rounded-borders shadow-1 border-grey"
            >
                <div class="row items-center justify-between q-mb-xs">
                    <div
                        class="text-weight-medium text-warning row items-center"
                    >
                        <q-spinner-dots
                            v-if="getActiveStepNumber() === 2"
                            size="sm"
                            class="q-mr-xs"
                            color="warning"
                        />
                        <q-spinner-gears
                            v-else-if="getActiveStepNumber() === 3"
                            size="sm"
                            class="q-mr-xs"
                            color="warning"
                        />
                        <q-spinner-hourglass
                            v-else
                            size="sm"
                            class="q-mr-xs"
                            color="warning"
                        />
                        <span class="text-weight-bold q-mr-xs"
                            >Step {{ getActiveStepNumber() }}:</span
                        >
                        {{ stageLabel }}
                    </div>
                    <div class="text-weight-bold text-warning">
                        {{ currentVideo.processing_progress || 0 }}%
                    </div>
                </div>
                <q-linear-progress
                    :value="(currentVideo.processing_progress || 0) / 100"
                    color="warning"
                    size="14px"
                    stripe
                    animated
                    class="rounded-borders"
                />
                <div class="text-caption text-grey-7 q-mt-xs">
                    {{ $t("onboarding.worker_processing") }}
                </div>
            </div>

            <q-banner
                v-else-if="currentVideo.status === 'ready'"
                class="bg-positive text-white rounded-borders q-mb-md shadow-1"
            >
                <template v-slot:avatar>
                    <q-icon name="check_circle" color="white" size="md" />
                </template>
                <div class="text-weight-bold text-subtitle1">
                    {{ $t("onboarding.video_ready") }}
                </div>
                <div class="text-caption">
                    All 4 steps completed (1. Upload ✓ 2. Check ✓ 3. 720p
                    Transcode ✓ 4. HLS Stream ✓)
                </div>
            </q-banner>

            <q-banner
                v-else-if="currentVideo.status === 'failed'"
                class="bg-negative text-white rounded-borders q-mb-md shadow-1"
            >
                <template v-slot:avatar>
                    <q-icon name="error" color="white" size="md" />
                </template>
                <div class="text-weight-bold">
                    {{ $t("onboarding.worker_failed") }}
                </div>
                <div>
                    {{
                        currentVideo.processing_error ||
                        $t("onboarding.processing_error_default") ||
                        "Video processing failed"
                    }}
                </div>
                <template v-slot:action>
                    <q-btn
                        flat
                        color="white"
                        :label="$t('onboarding.retry_processing')"
                        @click="retryProcessing"
                    />
                </template>
            </q-banner>

            <!-- Technical Video Information Card -->
            <div
                v-if="
                    currentVideo &&
                    (currentVideo.video_codec ||
                        currentVideo.width ||
                        currentVideo.file_size)
                "
                class="q-mt-md q-pa-md bg-white rounded-borders shadow-1 border-grey"
            >
                <div class="row items-center justify-between q-mb-sm">
                    <div
                        class="text-subtitle2 text-primary row items-center text-weight-bold"
                    >
                        <q-icon
                            name="info"
                            size="20px"
                            class="q-mr-xs"
                            color="primary"
                        />
                        {{
                            $t("onboarding.video_info_title") ||
                            "Video Information & Technical Details"
                        }}
                    </div>
                    <q-badge
                        color="blue-2"
                        text-color="blue-9"
                        class="text-weight-medium"
                    >
                        {{
                            currentVideo.status === "ready"
                                ? "Verified & Converted"
                                : "Analysis Complete"
                        }}
                    </q-badge>
                </div>

                <div class="row q-col-gutter-sm q-mt-xs">
                    <!-- Resolution & Aspect Ratio -->
                    <div class="col-6 col-sm-3">
                        <div
                            class="q-pa-xs bg-grey-2 rounded-borders text-center"
                        >
                            <div class="text-caption text-grey-7">
                                {{
                                    $t("onboarding.resolution") || "Resolution"
                                }}
                            </div>
                            <div
                                class="text-weight-bold text-subtitle2 text-dark"
                            >
                                {{
                                    currentVideo.width && currentVideo.height
                                        ? `${currentVideo.width} × ${currentVideo.height}`
                                        : "N/A"
                                }}
                            </div>
                            <div
                                v-if="currentVideo.aspect_ratio"
                                class="text-caption text-grey-8"
                            >
                                ({{ currentVideo.aspect_ratio }})
                            </div>
                        </div>
                    </div>

                    <!-- Video Codec -->
                    <div class="col-6 col-sm-3">
                        <div
                            class="q-pa-xs bg-grey-2 rounded-borders text-center"
                        >
                            <div class="text-caption text-grey-7">
                                {{
                                    $t("onboarding.video_codec") ||
                                    "Video Codec"
                                }}
                            </div>
                            <div
                                class="text-weight-bold text-subtitle2 text-dark"
                            >
                                {{
                                    currentVideo.video_codec
                                        ? currentVideo.video_codec.toUpperCase()
                                        : "N/A"
                                }}
                            </div>
                            <div
                                v-if="currentVideo.frame_rate"
                                class="text-caption text-grey-8"
                            >
                                {{ currentVideo.frame_rate }} fps
                            </div>
                        </div>
                    </div>

                    <!-- Audio Codec -->
                    <div class="col-6 col-sm-3">
                        <div
                            class="q-pa-xs bg-grey-2 rounded-borders text-center"
                        >
                            <div class="text-caption text-grey-7">
                                {{
                                    $t("onboarding.audio_codec") ||
                                    "Audio Codec"
                                }}
                            </div>
                            <div
                                class="text-weight-bold text-subtitle2 text-dark"
                            >
                                {{
                                    currentVideo.audio_codec
                                        ? currentVideo.audio_codec.toUpperCase()
                                        : "N/A"
                                }}
                            </div>
                            <div class="text-caption text-grey-8">
                                {{
                                    currentVideo.bitrate
                                        ? formatBitrate(currentVideo.bitrate)
                                        : "Standard"
                                }}
                            </div>
                        </div>
                    </div>

                    <!-- File Size & Duration -->
                    <div class="col-6 col-sm-3">
                        <div
                            class="q-pa-xs bg-grey-2 rounded-borders text-center"
                        >
                            <div class="text-caption text-grey-7">
                                {{
                                    $t("onboarding.file_size") ||
                                    "File Size & Length"
                                }}
                            </div>
                            <div
                                class="text-weight-bold text-subtitle2 text-dark"
                            >
                                {{ formatFileSize(currentVideo.file_size) }}
                            </div>
                            <div
                                v-if="currentVideo.duration"
                                class="text-caption text-grey-8"
                            >
                                {{ formatDuration(currentVideo.duration) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Poster Image Section -->
            <div
                class="q-mt-md q-pa-md bg-white rounded-borders shadow-1 border-grey"
            >
                <div class="text-subtitle2 q-mb-sm">
                    {{ $t("onboarding.upload_poster_image") }}
                </div>

                <div
                    v-if="currentVideo.poster_path"
                    class="q-mb-sm text-center"
                >
                    <q-img
                        :src="`/api/videos/${currentVideo.id}/poster?t=${posterTimestamp}`"
                        ratio="16/9"
                        style="max-width: 280px; border-radius: 6px"
                    />
                </div>

                <div
                    v-if="isUploadingPoster"
                    class="q-mb-sm text-center text-warning row items-center justify-center"
                >
                    <q-spinner-dots size="sm" class="q-mr-xs" color="warning" />
                    <span class="text-caption text-weight-medium">{{
                        $t("onboarding.uploading_poster") ||
                        "Uploading poster image..."
                    }}</span>
                </div>

                <q-file
                    v-model="selectedPoster"
                    :label="$t('onboarding.upload_poster_image')"
                    outlined
                    dense
                    accept="image/*"
                    bg-color="white"
                    :disable="isUploadingPoster"
                >
                    <template v-slot:append>
                        <q-btn
                            dense
                            flat
                            icon="upload"
                            :loading="isUploadingPoster"
                            :disable="!selectedPoster || isUploadingPoster"
                            @click="uploadPoster"
                        />
                    </template>
                </q-file>
            </div>

            <!-- Action Buttons -->
            <div class="row justify-between items-center q-mt-md">
                <q-btn
                    flat
                    color="negative"
                    icon="refresh"
                    :label="$t('onboarding.remove_and_reupload')"
                    :disable="isUploadingPoster || uploading"
                    @click="replaceVideo"
                />
                <q-btn
                    v-if="props.showUseVideoBtn"
                    color="secondary"
                    icon="check"
                    :label="$t('onboarding.use_this_video')"
                    :disable="isProcessing || isUploadingPoster"
                    @click="emitSelection"
                />
            </div>
        </div>

        <!-- Estimated Waiting Time & Email Notification Dialog -->
        <q-dialog v-model="showEstimateDialog" persistent>
            <q-card style="min-width: 350px; max-width: 500px" class="q-pa-md">
                <q-card-section class="row items-center q-pb-none">
                    <div class="text-h6 text-primary row items-center">
                        <q-icon
                            name="schedule"
                            class="q-mr-xs"
                            color="primary"
                        />
                        {{ $t("onboarding.estimated_wait_title") }}
                    </div>
                    <q-space />
                    <q-btn icon="close" flat round dense v-close-popup />
                </q-card-section>

                <q-card-section class="q-pt-md">
                    <p class="text-body2 text-grey-8 q-mb-md">
                        {{ $t("onboarding.estimated_wait_desc") }}
                    </p>

                    <q-list separator class="bg-grey-2 rounded-borders q-mb-md">
                        <q-item>
                            <q-item-section avatar>
                                <q-icon
                                    name="format_list_numbered"
                                    color="secondary"
                                />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label class="text-weight-medium">
                                    {{
                                        $t("onboarding.queue_ahead", {
                                            count:
                                                queueEstimate.queue_length || 0,
                                        })
                                    }}
                                </q-item-label>
                            </q-item-section>
                        </q-item>

                        <q-item>
                            <q-item-section avatar>
                                <q-icon name="sd_storage" color="secondary" />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label class="text-weight-medium">
                                    {{
                                        $t("onboarding.file_size_info", {
                                            size:
                                                queueEstimate.file_size_mb || 0,
                                        })
                                    }}
                                </q-item-label>
                            </q-item-section>
                        </q-item>

                        <q-item>
                            <q-item-section avatar>
                                <q-icon name="hourglass_top" color="primary" />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label
                                    class="text-weight-bold text-primary text-subtitle1"
                                >
                                    {{
                                        $t("onboarding.approx_time", {
                                            time:
                                                queueEstimate.formatted_time ||
                                                "...",
                                        })
                                    }}
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-list>

                    <!-- Email Notification Box -->
                    <div class="bg-blue-1 q-pa-md rounded-borders">
                        <div class="text-subtitle2 text-primary q-mb-xs">
                            {{ $t("onboarding.notify_email_prompt") }}
                        </div>
                        <div class="row q-col-gutter-sm items-center q-mt-xs">
                            <div class="col">
                                <q-input
                                    v-model="notificationEmail"
                                    :label="$t('onboarding.email_placeholder')"
                                    outlined
                                    dense
                                    bg-color="white"
                                    type="email"
                                />
                            </div>
                            <div class="col-auto">
                                <q-btn
                                    color="primary"
                                    icon="mail"
                                    :label="$t('onboarding.button_notify')"
                                    :loading="savingEmail"
                                    :disable="!notificationEmail"
                                    @click="submitNotificationEmail"
                                />
                            </div>
                        </div>
                    </div>
                </q-card-section>

                <q-card-actions align="right">
                    <q-btn color="primary" flat label="OK" v-close-popup />
                </q-card-actions>
            </q-card>
        </q-dialog>
    </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, watch, getCurrentInstance } from "vue";
import axios from "axios";
import { useQuasar } from "quasar";

const { proxy } = getCurrentInstance();
const t = (key, replacements) => proxy?.$t(key, replacements) || key;

const props = defineProps({
    initialVideo: {
        type: Object,
        default: null,
    },
    showUseVideoBtn: {
        type: Boolean,
        default: true,
    },
    userEmail: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["uploaded", "selected", "updated", "reset"]);
const $q = useQuasar();

const selectedFile = ref(null);
const selectedPoster = ref(null);
const posterTimestamp = ref(Date.now());
const videoTitle = ref("");
const uploading = ref(false);
const isUploadingPoster = ref(false);
const uploadProgress = ref(0);
const currentChunk = ref(0);
const totalChunks = ref(0);
const currentVideo = ref(null);
const existingVideoId = ref(null);

const showEstimateDialog = ref(false);
const queueEstimate = ref({});
const notificationEmail = ref("");
const savingEmail = ref(false);

const isRetrying = ref(false);
const retryAttemptCount = ref(0);

const chunkSize = 1 * 1024 * 1024; // 1 MB fixed binary chunk size
let pollTimer = null;

const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    const mb = bytes / (1024 * 1024);
    if (mb >= 1000) {
        return (mb / 1024).toFixed(2) + " GB";
    }
    return mb.toFixed(1) + " MB";
};

const formatBitrate = (bitsPerSec) => {
    if (!bitsPerSec) return "N/A";
    const kbps = bitsPerSec / 1000;
    if (kbps >= 1000) {
        return (kbps / 1000).toFixed(2) + " Mbps";
    }
    return Math.round(kbps) + " kbps";
};

const formatDuration = (seconds) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

const isProcessing = computed(() => {
    if (!currentVideo.value) return false;
    const status = currentVideo.value.status;
    const stage = currentVideo.value.processing_stage;
    if (status === "ready" || stage === "ready" || status === "failed") {
        return false;
    }
    return true;
});

const stopPollingStatus = () => {
    if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
    }
};

let isFetchingStatus = false;

const fetchWorkerStatus = async () => {
    if (
        !currentVideo.value ||
        !currentVideo.value.id ||
        isFetchingStatus ||
        isUploadingPoster.value
    )
        return;

    isFetchingStatus = true;
    try {
        const response = await axios.get(
            `/api/videos/${currentVideo.value.id}/status`,
        );
        if (response.data && response.data.video) {
            const fresh = response.data.video;
            const currentPoster = currentVideo.value?.poster_path;
            currentVideo.value = {
                ...fresh,
                poster_path: fresh.poster_path || currentPoster || null,
            };
            if (fresh.poster_path && fresh.poster_path !== currentPoster) {
                posterTimestamp.value = Date.now();
            }
            emit("updated", currentVideo.value);
            if (!isProcessing.value) {
                stopPollingStatus();
                emit("selected", currentVideo.value);
            }
        }
    } catch (e) {
        // Silently swallow polling errors
    } finally {
        isFetchingStatus = false;
    }
};

const startPollingStatus = () => {
    if (pollTimer) return;
    fetchWorkerStatus();
    pollTimer = setInterval(fetchWorkerStatus, 2000);
};

watch(
    () => props.initialVideo,
    (newVal) => {
        currentVideo.value = newVal ? { ...newVal } : null;
        videoTitle.value = newVal ? newVal.title : "";
        existingVideoId.value = newVal ? newVal.id : null;
        selectedFile.value = null;
        selectedPoster.value = null;
        posterTimestamp.value = Date.now();
        if (currentVideo.value && currentVideo.value.id) {
            if (isProcessing.value) {
                startPollingStatus();
            } else {
                fetchWorkerStatus();
            }
        }
    },
    { immediate: true },
);

const stageLabel = computed(() => {
    if (!currentVideo.value) return t("onboarding.worker_processing");
    const stage =
        currentVideo.value.processing_stage || currentVideo.value.status;
    switch (stage) {
        case "upload_complete":
            return t("onboarding.stage_upload_complete");
        case "verifying_bitstream":
            return t("onboarding.stage_verifying_bitstream");
        case "transcoding_web_mp4":
            return (
                t("onboarding.stage_transcoding_web_mp4") ||
                "Optimizing video for web playback"
            );
        case "generating_hls":
            return t("onboarding.stage_generating_hls");
        case "repairing_stream":
            return t("onboarding.stage_repairing_stream");
        default:
            return t("onboarding.stage_default_processing");
    }
});

const getActiveStepNumber = () => {
    if (uploading.value) return 1;
    if (!currentVideo.value) return 1;
    const stage = currentVideo.value.processing_stage;
    const status = currentVideo.value.status;

    if (
        stage === "upload_complete" ||
        stage === "verifying_bitstream" ||
        stage === "repairing_stream" ||
        status === "checking" ||
        status === "repairing"
    ) {
        return 2;
    }
    if (stage === "transcoding_web_mp4") {
        return 3;
    }
    if (
        stage === "generating_hls" ||
        status === "encoding" ||
        status === "processing"
    ) {
        return 4;
    }
    if (status === "ready" || stage === "ready") {
        return 4;
    }
    return 2;
};

const getStepStatus = (stepNum) => {
    if (stepNum === 1) {
        if (uploading.value) return "active";
        if (
            currentVideo.value &&
            (currentVideo.value.file_path ||
                currentVideo.value.processing_stage ||
                ["checking", "encoding", "ready", "failed"].includes(
                    currentVideo.value.status,
                ))
        ) {
            return "completed";
        }
        return "active";
    }

    if (stepNum === 2) {
        if (!currentVideo.value || uploading.value) return "pending";

        const status = currentVideo.value.status;
        const stage = currentVideo.value.processing_stage;

        if (
            ["transcoding_web_mp4", "generating_hls", "ready"].includes(
                stage,
            ) ||
            (status === "ready" && stage !== "verifying_bitstream")
        ) {
            return "completed";
        }
        if (
            status === "failed" &&
            (stage === "verifying_bitstream" ||
                stage === "repairing_stream" ||
                stage === "upload_complete" ||
                status === "checking" ||
                status === "repairing")
        ) {
            return "failed";
        }
        if (
            status === "checking" ||
            status === "repairing" ||
            stage === "verifying_bitstream" ||
            stage === "repairing_stream" ||
            stage === "upload_complete"
        ) {
            return "active";
        }
        return "pending";
    }

    if (stepNum === 3) {
        if (!currentVideo.value || uploading.value) return "pending";

        const status = currentVideo.value.status;
        const stage = currentVideo.value.processing_stage;

        if (
            stage === "generating_hls" ||
            stage === "ready" ||
            status === "ready"
        ) {
            return "completed";
        }
        if (status === "failed" && stage === "transcoding_web_mp4") {
            return "failed";
        }
        if (stage === "transcoding_web_mp4") {
            return "active";
        }
        if (!stage && (status === "encoding" || status === "processing")) {
            const progress = currentVideo.value.processing_progress || 0;
            if (progress >= 60) return "completed";
            return "active";
        }
        return "pending";
    }

    if (stepNum === 4) {
        if (!currentVideo.value || uploading.value) return "pending";

        const status = currentVideo.value.status;
        const stage = currentVideo.value.processing_stage;

        if (status === "ready" || stage === "ready") {
            return "completed";
        }
        if (status === "failed" && stage === "generating_hls") {
            return "failed";
        }
        if (stage === "generating_hls") {
            return "active";
        }
        if (!stage && (status === "encoding" || status === "processing")) {
            const progress = currentVideo.value.processing_progress || 0;
            if (progress >= 60) return "active";
        }
        return "pending";
    }

    return "pending";
};

const getStepClass = (stepNum) => {
    const status = getStepStatus(stepNum);
    return {
        "step-completed": status === "completed",
        "step-active": status === "active",
        "step-failed": status === "failed",
        "step-pending": status === "pending",
    };
};

const getStepTextClass = (stepNum) => {
    const status = getStepStatus(stepNum);
    if (status === "completed") return "text-positive";
    if (status === "active") return "text-warning";
    if (status === "failed") return "text-negative";
    return "text-grey-6";
};

watch(
    currentVideo,
    (newVal) => {
        if (newVal && isProcessing.value) {
            startPollingStatus();
        } else {
            stopPollingStatus();
        }
    },
    { immediate: true },
);

onBeforeUnmount(() => {
    stopPollingStatus();
});

const saveTitle = async () => {
    if (!videoTitle.value || !currentVideo.value) return;

    try {
        const response = await axios.put(
            `/api/videos/${currentVideo.value.id}`,
            {
                title: videoTitle.value,
            },
        );
        if (response.data && response.data.video) {
            currentVideo.value = response.data.video;
        } else {
            currentVideo.value.title = videoTitle.value;
        }
        $q.notify({
            type: "positive",
            message: t("onboarding.video_updated") || "Video updated",
        });
        emit("updated", currentVideo.value);
        emit("selected", currentVideo.value);
    } catch (e) {
        $q.notify({
            type: "negative",
            message:
                t("onboarding.error_updating_video") || "Error updating video",
        });
    }
};

const postWithRetry = async (url, formData, options = {}, maxRetries = 5) => {
    let lastError;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const response = await axios.post(url, formData, options);
            isRetrying.value = false;
            retryAttemptCount.value = 0;
            return response;
        } catch (error) {
            lastError = error;
            if (attempt < maxRetries) {
                isRetrying.value = true;
                retryAttemptCount.value = attempt;
                const delay = Math.min(1000 * Math.pow(1.5, attempt - 1), 5000);
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }
    }
    isRetrying.value = false;
    throw lastError;
};

const startChunkedUpload = async () => {
    if (!selectedFile.value) return;

    const file = selectedFile.value;
    const size = file.size;
    totalChunks.value = Math.ceil(size / chunkSize);
    uploading.value = true;
    uploadProgress.value = 0;
    currentChunk.value = 0;
    isRetrying.value = false;

    let videoId = currentVideo.value
        ? currentVideo.value.id
        : existingVideoId.value;

    try {
        for (let index = 0; index < totalChunks.value; index++) {
            currentChunk.value = index;

            const start = index * chunkSize;
            const end = Math.min(size, start + chunkSize);
            const blobChunk = file.slice(start, end);

            const formData = new FormData();
            formData.append("file", blobChunk, file.name);
            formData.append("chunkIndex", index);
            formData.append("totalChunks", totalChunks.value);
            if (videoTitle.value) {
                formData.append("title", videoTitle.value);
            }
            if (videoId) {
                formData.append("videoId", videoId);
            }

            const response = await postWithRetry(
                "/api/videos/upload-chunk",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                },
                5,
            );

            if (response.data && response.data.video) {
                videoId = response.data.video.id;
                currentVideo.value = response.data.video;
                existingVideoId.value = response.data.video.id;
            }

            uploadProgress.value = Math.round(
                ((index + 1) / totalChunks.value) * 100,
            );
        }

        $q.notify({
            type: "positive",
            message:
                t("onboarding.video_upload_completed") ||
                "Video upload completed! Background worker started.",
        });
        emit("uploaded", currentVideo.value);
        emit("selected", currentVideo.value);
        emit("updated", currentVideo.value);
        startPollingStatus();

        if (selectedPoster.value && currentVideo.value) {
            await uploadPoster();
        }

        await fetchQueueEstimate(currentVideo.value);
    } catch (e) {
        $q.notify({
            type: "negative",
            message:
                t("onboarding.error_uploading_chunk") ||
                "Error uploading video chunk.",
        });
    } finally {
        uploading.value = false;
    }
};

const fetchQueueEstimate = async (video) => {
    if (!video) return;
    try {
        const response = await axios.get(
            `/api/videos/${video.id}/queue-estimate`,
        );
        if (response.data && response.data.estimate) {
            queueEstimate.value = response.data.estimate;
            if (response.data.user_email) {
                notificationEmail.value = response.data.user_email;
            } else if (props.userEmail) {
                notificationEmail.value = props.userEmail;
            }
            showEstimateDialog.value = true;
        }
    } catch (e) {
        // Silently swallow estimate fetch errors
    }
};

const submitNotificationEmail = async () => {
    if (!notificationEmail.value || !currentVideo.value) return;

    savingEmail.value = true;
    try {
        await axios.post(`/api/videos/${currentVideo.value.id}/notify-email`, {
            email: notificationEmail.value,
        });
        $q.notify({
            type: "positive",
            message:
                t("onboarding.notify_success") ||
                "E-Mail-Benachrichtigung wurde aktiviert!",
        });
        showEstimateDialog.value = false;
    } catch (e) {
        $q.notify({
            type: "negative",
            message:
                t("validation.email") ||
                "Bitte eine gültige E-Mail-Adresse eingeben.",
        });
    } finally {
        savingEmail.value = false;
    }
};

const retryProcessing = async () => {
    if (!currentVideo.value) return;

    try {
        const response = await axios.post(
            `/api/videos/${currentVideo.value.id}/process`,
        );
        currentVideo.value = response.data.video;
        emit("updated", currentVideo.value);
        startPollingStatus();
        $q.notify({
            type: "info",
            message:
                t("onboarding.redispatched_worker") ||
                "Re-dispatched background worker.",
        });
    } catch (e) {
        $q.notify({
            type: "negative",
            message:
                t("onboarding.failed_redispatch_worker") ||
                "Failed to re-dispatch worker.",
        });
    }
};

const uploadPoster = async () => {
    if (!selectedPoster.value || !currentVideo.value) return;

    isUploadingPoster.value = true;
    const formData = new FormData();
    formData.append("poster", selectedPoster.value);

    try {
        const response = await axios.post(
            `/api/videos/${currentVideo.value.id}/poster`,
            formData,
            {
                headers: { "Content-Type": "multipart/form-data" },
            },
        );
        if (response.data && response.data.video) {
            const fresh = response.data.video;
            currentVideo.value = {
                ...currentVideo.value,
                poster_path: fresh.poster_path,
                updated_at: fresh.updated_at,
            };
            selectedPoster.value = null;
            posterTimestamp.value = Date.now();
            $q.notify({
                type: "positive",
                message: t("onboarding.poster_uploaded") || "Poster uploaded!",
            });
            emit("updated", currentVideo.value);
        }
    } catch (e) {
        $q.notify({
            type: "negative",
            message:
                t("onboarding.poster_upload_failed") || "Poster upload failed.",
        });
    } finally {
        isUploadingPoster.value = false;
    }
};

const emitSelection = () => {
    emit("selected", currentVideo.value);
};

const replaceVideo = () => {
    stopPollingStatus();
    selectedFile.value = null;
    selectedPoster.value = null;
    currentVideo.value = null;
    existingVideoId.value = null;
    uploadProgress.value = 0;
};

const cancelReplace = () => {
    if (props.initialVideo) {
        currentVideo.value = { ...props.initialVideo };
        videoTitle.value = props.initialVideo.title;
        existingVideoId.value = props.initialVideo.id;
    }
};
</script>

<style scoped>
.video-stepper {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #ffffff;
}

.border-grey {
    border: 1px solid #e2e8f0;
}

.step-box {
    padding: 10px 6px;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.step-box.step-active {
    background-color: #fff8f0;
    box-shadow: inset 0 0 0 1.5px #f59e0b;
}

.step-box.step-completed {
    background-color: #f0fff4;
}

.step-box.step-failed {
    background-color: #fff5f5;
    box-shadow: inset 0 0 0 1px #e53e3e;
}

.step-icon-container {
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
}

.step-connector {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
}

.connector-line {
    width: 100%;
    height: 3px;
    background-color: #e2e8f0;
    border-radius: 2px;
    transition: background-color 0.3s ease;
}

.connector-line.line-done {
    background-color: #38a169;
}

.step-title {
    font-size: 0.875rem;
    line-height: 1.25;
}

.step-caption {
    font-size: 0.75rem;
    line-height: 1.1;
    margin-top: 3px;
}
</style>
